// @ts-nocheck

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { wagmiConfig } from "@config";
import moment from "moment";

import { getChainedToken, getChainedTokens, isValidChain, lookupContract, type TokenDefinition } from "@bracket/addressbook";
import { bracket, } from "@packageconfig";

import * as R from "./resolvers";
import { TOKENS } from "@const";

import {
	bi,
	// getChainedTokenLabels, TODO: remove when testing passes.
	n,
	roundToBalance,
	sn,
} from "@helpers";

import { getDefaultTokens, makeCancellablePromise } from "./helpers";

import { event as GAEvent } from "react-ga";
import type { ResolverResult } from "./resolvers/types";

import { getAccount, getBalance, getChainId, watchAccount, type GetAccountReturnType } from "wagmi/actions";

import type { ExecutableToken, SwappableToken, NumericString } from "@types";
import { ParseRpcError } from "viem";

export declare type TransactionReult = any; //TransactionFailureResult | TransactionSuccessResult;

export declare type TransactionSuccessResult = {
	status: "success";
	message: string;
	hash: string;
};

export declare type TransactionFailureResult = {
	status: "error";
	message: string;
};

export declare type Notification = {
	severity: "error" | "warning" | "success";
	message: string;
	code: "SUCCESS" | "CANCELLED" | "UNDERFUND" | "UNHANDLED";
};

export declare type EscrowStore = {
	/** WALLET balances for all tokens */
	availables: Partial<Record<string, bigint>>;
	availables_since: number;

	/** STAKE balances for all tokens */
	balances: Partial<Record<string, bigint>>;
	balances_since: number;

	/** busy flag, blur the background, do whatever, dont let the user fuck around. */
	busy: boolean;

	/** flag that this instance is in an errant state */
	errant: boolean;

	/** the error message that was provided when this instance enterred an errant state */
	error_message: string;

	/** an array of information about how/what/why this instance is in an errant state */
	error_detail: string[];

	/** the bracket offer price for ethereum */
	ethereum_price: bigint;
	ethereum_price_since: number;

	/** the current wallet balance of ethereum */
	ethereum_balance: bigint;
	ethereum_balance_since: number;

	/** flag that this instance is now initialized */
	initialized: boolean;

	/** flag that this instance is in the process of initialization */
	initializing: boolean;

	/** the total claimr point balance */
	claimr_points: number;

	/** the amount the current user has staked */
	user_stake: bigint;
	user_stake_since: number;

	/** the amount the current user has staked on the current chain. */
	user_stake_chain: bigint;
	user_stake_chain_since: number;

	/** the amount the system has staked in USD */
	total_stake: bigint;
	total_stake_since: number;

	/** conversion rates for all tokens */
	rates: Partial<Record<string, bigint>>;
	rates_since: number;

	/** the token which the user will provide a deposit */
	depositToken: TokenDefinition | null;

	/** the amount being deposited in in count of the token */
	depositAmount: NumericString;

	depositAmountValid: boolean;
	depositAmountMessage: string;

	/** the token which the user will provide a withdrawl */
	withdrawInToken: TokenDefinition | null;

	/** the token which the user will provide a withdrawl */
	// withdrawOutToken: AnyToken;

	/** an amount in the depositInToken */
	withdrawAmount: NumericString;

	/** active token collection
	 * @deprecated - moving this over to bracket's token address book */
	tokens: TokenDefinition[];

	swap_tokens: string[];

	/** swapping, if ETH or WETH as the withdrawing token */
	swapping: boolean;

	swapDepositToken: TokenDefinition | null;
	swapDepositAmount: NumericString;
	swapWithdrawToken: TokenDefinition | null;
	swapWithdrawAmount: NumericString;
	swapWithdrawAmountValid: boolean;
	swapWithdrawAmountMessage: string;

	season_conclude_date: string;

	quote_issue: boolean;

	slippage: string;
	swap_minimum: number;
	impact: string;

	initialize: () => Promise<void>;
	deinitialize: () => void;
	balanceUSD: (token: TokenDefinition) => bigint;
	cancelDataLoad: (() => void) | null;
	getTokenBalance: (token: TokenDefinition) => bigint;
	getTokenAvailable: (token: TokenDefinition) => bigint;
	getTokenBalanceUSD: (token: TokenDefinition) => bigint;

	setDepositToken: (token: TokenDefinition) => void;
	setDepositAmount: (amount: NumericString) => void;
	setWithdrawInToken: (token: TokenDefinition) => void;
	setWithdrawAmount: (amount: NumericString) => void;

	setSwapWithdrawToken: (token: TokenDefinition) => void;
	setSwapWithdrawAmount: (amount: NumericString, silent?: boolean) => Promise<void>;
	setSwapDepositToken: (token: TokenDefinition) => void;
	setSwapDepositAmount: (amount: NumericString) => void;

	deposit: (signer: () => Promise<[boolean, string?]> | null) => Promise<ResolverResult>;
	swap: (signer: () => Promise<[boolean, string?]> | null) => Promise<ResolverResult>;
	withdraw: () => Promise<ResolverResult>;

	/** reset the instance. */
	reset: () => void;

	/** set the deposit amount to the maximum available for the current token */
	applyMaxDeposit: () => void;

	/** set the swap deposit amount to the maximum available for the current token */
	applyMaxSwap: () => void;

	/** set the swap withdrawal amount to the maximum available for the current token */
	applyMaxWithdraw: () => void;

	/** helper, convert any amount in a non USD token to USD */
	toUSD: (token: TokenDefinition, amount: bigint) => bigint;

	_accountListener: () => void;
	_initializePolling: () => void;
	_loadData: (force?: boolean) => Promise<void>;
	_loadEthereumBalance: (force?: boolean) => Promise<void>;
	_loadEthereumPrice: (force?: boolean) => Promise<void>;
	_loadUserBalances: () => Promise<void>;
	_loadAvailables: (force?: boolean) => Promise<void>;
	_loadTotalStaked: (force?: boolean) => Promise<void>;
	__loadTotalUserStaked: (force?: boolean) => Promise<void>;
	_loadTotalUserStakedOnChain: (force?: boolean) => Promise<void>;
	_onAccountChange: (state: GetAccountReturnType, prev_state: GetAccountReturnType) => Promise<void>;
	_poll: () => Promise<void>;
	_pollInterval: NodeJS.Timeout | null;
	_resetData: () => Promise<void>;

	/** reset swap input */
	_resetSwap: () => void;

	/** reset deposit input */
	_resetDeposit: () => void;

	_stopPolling: () => void;
	_toggleBusy: () => void;
	_cancelDataLoad: () => void;
};

// replace this with getChainedTokens.
const shim: any = Object.assign(
	{},
	Object.keys(TOKENS).reduce((acc, k) => Object.assign(acc, { [k]: BigInt(-1) }), {}),
);

const POLL_INTERVAL = 10000;

const GAS_FEE_MAP = new Map<number, number>([
	[1, 0.01],
	[42161, 0.001]
]);


// export const useEscrowStore = create<EscrowStore>()(persist((set, get,) => ({
export const useEscrowStore = create<EscrowStore>()((set, get) => ({
	availables: shim,
	availables_since: -1,

	balances: shim,
	balances_since: -1,
	busy: false as boolean,

	claimr_points: -1,

	depositAmount: "",
	depositAmountValid: true,
	depositAmountMessage: "",
	depositOutToken: "stETH",
	depositToken: null,

	errant: false,
	error_message: "",
	error_detail: [],

	ethereum_balance: BigInt(-1),
	ethereum_balance_since: -1,

	ethereum_price: BigInt(-1),
	ethereum_price_since: -1,

	initialized: false,
	initializing: false,

	rates: shim,
	rates_since: -1,

	season_conclude_date: moment("20241030").format(),

	swap_tokens: ["ETH", "WETH"],
	swapDepositAmount: "",
	swapDepositToken: null,
	swapping: true,
	swapWithdrawAmount: "",
	swapWithdrawAmountValid: true,
	swapWithdrawAmountMessage: "",
	swapWithdrawToken: null,
	swapWithdrawImpact: -1,
	tokens: [],

	total_stake: BigInt(-1),
	total_since: -1,

	withdrawAmount: "",
	withdrawInToken: null,

	user_stake: BigInt(-1),
	user_stake_since: -1,

	user_stake_chain: BigInt(-1),
	user_stake_chain_since: -1,

	slippage: "0",
	impact: "0",

	_accountListener: null!,
	_pollInterval: null,

	cancelDataLoad: null,

	initialize: async () => {
		const { initialized, initializing, _loadData, _initializePolling } = get();

		if (!initialized && !initializing) {
			get()._loadChainedTokens();
			set({
				initializing: true,
				busy: true,
			});

			// load up the data and commence polling for changes
			await _loadData();
			_initializePolling();

			const swapping = get().swap_tokens.includes(get().depositToken.label);

			set({
				_accountListener: watchAccount(wagmiConfig, {
					onChange: get()._onAccountChange,
				}),
				initializing: false,
				initialized: true,
				busy: false,
				swapping,
			});
			return;
		}
	},

	// deinitialize/destruct/onComponentDidUnmount cleanup.
	deinitialize: () => {
		const { _accountListener, _stopPolling, _resetSwap } = get();

		if (_accountListener) _accountListener();

		_stopPolling();
		_resetSwap();

		set({
			_accountListener: null,
			initialized: false,
			initializing: false,
		});
	},

	_loadChainedTokens: () => {
		const chainId = getChainId(wagmiConfig);

		if (chainId) {
			const tokendata = getChainedTokens(chainId, { includeETH: true });

			const next = tokendata.concat();
			const swapables = tokendata.filter((t) => t.swapable).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
			const inactives = tokendata.filter((t) => t.inactive).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
			const others = tokendata.filter((t) => !t.swapable && !t.inactive).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
			const tokens = [...swapables, ...others, ...inactives].filter((tok) => tok.disabled !== true);

			const [depositLabel, swapDepositLabel, swapWithdrawalLabel] = getDefaultTokens();

			// set the default tokens
			const depositToken = getChainedToken(chainId, depositLabel);
			const swapDepositToken = getChainedToken(chainId, swapDepositLabel);
			const swapWithdrawToken = getChainedToken(chainId, swapWithdrawalLabel);

			set({
				tokens,
				swapDepositToken,
				swapWithdrawToken,
				depositToken,
			});
		}
	},

	reset: () => {
		set({
			swapDepositAmount: "",
			swapWithdrawAmount: "",
			depositAmount: "",
			withdrawAmount: "",
		});
	},

	getTokenBalance: (token) => {
		const {
			ethereum_balance,
			balances,
			availables,
		} = get();

		switch (token.label) {
			case "ETH":
				return ethereum_balance;
			default:
				if (token.label in availables && typeof availables[token.label as ExecutableToken] === "bigint") {
					return availables[token.label as ExecutableToken] as bigint;
				}
				// if (token.label in balances && typeof balances[token.label as ExecutableToken] === "bigint") {
				// 	return balances[token.label as ExecutableToken] as bigint;
				// }
				throw new Error(`no balance available for "${token.label}"`);
		}
	},

	balanceUSD: (token) => {
		const { balances, ethereum_price, rates } = get();

		const balance = balances[token.label];
		const rate = rates[token.label];

		if (typeof balance === "bigint" && typeof rate === "bigint") {
			// burn off dust.
			if (n(balance) < 0.00001) return bi(0);
			return bi(n(ethereum_price) * n(balance) * n(rate));
		}
		console.warn(`EscrowStore::balanceUSD -- received an invalid rate or balance for token ${token.label}`);
		return bi(0);
	},

	setDepositToken: (token) => {
		const { swap_tokens, swapDepositToken } = get();
		const swapping = swap_tokens.includes(token.label as any) || swap_tokens.includes(token.symbol as any);

		if (swapping) {
			// deposit in becomes withdraw out ( the token will be ETH or WETH, thus indicating we're now swapping)
			const swapWithdrawToken = token;
			const swapWithdrawAmount = "";
			// const swapDepositToken = getChainedToken(getChainId(), "rETH");
			const swapDepositAmount = "";

			set({
				swapWithdrawToken,
				swapWithdrawAmount,
				swapDepositToken,
				swapDepositAmount,
				depositAmountValid: true,
				depositAmountMessage: "",
				swapping,
			});
		} else {
			set({
				depositToken: token,
				depositAmount: "",
				depositAmountValid: true,
				depositAmountMessage: "",
				swapping,
			});
		}
	},

	setDepositAmount: (depositAmount) => {
		try {
			const { depositToken, toUSD, availables } = get();

			if (!depositToken) throw new Error("deposit token not set.");
			if (typeof availables[depositToken.label] !== "bigint") throw new Error(`failed gaining available amount for token "${depositToken.label}"`);

			let depositAmountMessage = "";
			let depositAmountValid = true;

			if (!Number.isNaN(depositAmount)) {
				const depositAmountNumBI = bi(Number(depositAmount));
				const depositAmountUSD = n(toUSD(depositToken, depositAmountNumBI));
				depositAmountValid = depositAmountUSD > 95 && depositAmountNumBI <= availables[depositToken.label];

				if (depositAmountUSD < 95) depositAmountMessage = "Minimum deposit is $100USD";
				else if (depositAmountNumBI > availables[depositToken.label]) depositAmountMessage = "Exceeds your balance";
			}

			set({
				depositAmount,
				depositAmountValid,
				depositAmountMessage,
			});
		} catch (err) {
			throw new Error(`EscrowStore::setDepositAmount errored - ${err.message}`);
		}
	},

	setWithdrawInToken: (withdrawInToken) => {
		set({ withdrawInToken });
	},

	setWithdrawAmount: (withdrawAmount) => {
		set({ withdrawAmount });
	},

	setSwapDepositAmount: (swapDepositAmount) => {
		set({ swapDepositAmount });
	},

	setSwapDepositToken: async (token, silent = false) => {

		const {
			swapWithdrawAmount: amount,
			rates,
			ethereum_price,
		} = get();

		set({ swapDepositToken: token });
		set({ swapWithdrawAmountValid: true, swapWithdrawAmountMessage: "" });

		if (!silent) {
			if (Number(amount) > 0) {
				const result = await R.quote({ token, amount });

				// manu will give us a zero if there is no liquidity.
				if (result <= 0) {
					set({
						showInvalid: true,
						swapWithdrawAmountMessage: "Not enough swap pool liquidity to swap",
						swapWithdrawAmountValid: false,
						quote_issue: true,
					});
					return;
				}

				const inputAsEth = Number(amount) * n(ethereum_price);
				const resultAsEth = n(result) * n(rates[token.label]) * n(ethereum_price);
				const [min, nSlippage, nImpact,] = R.calcMinAndSlippage(inputAsEth, resultAsEth);
				if(nImpact > Number.parseFloat(bracket.slippage)) {
					set({
						showInvalid: true,
						swapWithdrawAmountMessage: "Not enough swap pool liquidity to swap",
						swapWithdrawAmountValid: false,
						quote_issue: true,
						slippage: nSlippage.toString(),
						impact: nImpact.toString(),
					});
					return;
				}

				const swapDepositAmount = Number(Number(result) / Number(`1e${token?.decimals}`)).toFixed(5);
				//TODO: what is swam_min for?
				set({
					swapDepositAmount,
					slippage: nSlippage.toString(),
					impact: nImpact.toString(),
					swap_minimum: min,
				});
			}
		}
	},

	setSwapWithdrawAmount: async (amount, silent = false) => {

		try {

			const {
				swapDepositToken,
				swapWithdrawToken,
				ethereum_balance,
				ethereum_price,
				rates,
				availables,
				toUSD,
			} = get();

			if(!swapDepositToken) throw new Error("swap deposit token not set");
			if(!swapWithdrawToken) throw new Error("swap withdrawal token not set");

			//TODO: 123
			if(n(rates[swapDepositToken.label]) <= 0.0001)
				throw new Error(`rate for token ${swapDepositToken.symbol} not set`);

			const validNumeric = !Number.isNaN(amount);
			let swapWithdrawAmountValid = true;
			let swapWithdrawAmountMessage = "";

			if (validNumeric) {
				const withdrawalAmount = bi(Number(amount));
				const withdrawBalance = swapWithdrawToken.symbol === "ETH" ? ethereum_balance : availables[swapWithdrawToken.label] + bi(0.0001);
				if (typeof withdrawBalance !== "bigint") throw new Error("swap failed determining withdrawal balance");

				const withdrawAmountUSD = toUSD(swapWithdrawToken, amount);

				swapWithdrawAmountValid = (withdrawAmountUSD > 95 || withdrawAmountUSD <= 0) && withdrawalAmount < withdrawBalance;
				swapWithdrawAmountMessage = swapWithdrawAmountValid ? "" : withdrawAmountUSD < 95 ? "Minimum deposit is $100USD" : withdrawalAmount > withdrawBalance ? "Exceeds your balance" : "";
			}

			set({
				swapWithdrawAmount: amount,
				swapWithdrawAmountValid,
				swapWithdrawAmountMessage,
			});

			if (!silent && validNumeric) {
				if (Number(amount) > 0) {

					const result = await R.quote({ token: swapDepositToken, amount });

					// manu will give us a zero if there is no liquidity.
					if (result <= 0) {
						set({
							showInvalid: true,
							swapWithdrawAmountMessage: "Not enough swap pool liquidity to swap",
							swapWithdrawAmountValid: false,
							quote_issue: true,
							slippage: "n/a",
							impact: "n/a"
						});
						return;
					}

					const inputAsEth = Number(amount) * n(ethereum_price);
					const resultAsEth = n(result) * n(rates[swapDepositToken.label]) * n(ethereum_price);
					const [min, nSlippage, nImpact,] = R.calcMinAndSlippage(inputAsEth, resultAsEth);
					const swapDepositAmount = Number(Number(result) / Number(`1e${swapDepositToken.decimals}`)).toFixed(5);
					
					if(nImpact > Number.parseFloat(bracket.slippage)) {
						set({
							showInvalid: true,
							swapWithdrawAmountMessage: "Not enough swap pool liquidity to swap",
							swapWithdrawAmountValid: false,
							quote_issue: true,
							slippage: nSlippage.toString(),
							impact: nImpact.toString(),
						});
						return;
					}

					set({
						swapDepositAmount,
						slippage: nSlippage.toString(),
						impact: nImpact.toString(),
						swap_minimum: min,
					});

				} else {
					// poor mans promise cancellation - we're waiting for prior keystroks on the qoute to come back first.
					setTimeout(() => {
						set({ swapDepositAmount: "" });
					}, 500);
				}
			}
			return;
		} catch (err) {
			return {
				status: "error",
				message: "There applying a quote",
				code: "UNHANDLED",
			};
		}
	},

	setSwapWithdrawToken: (token) => {
		const { swap_tokens } = get();
		const swapping = swap_tokens.includes(token.label as SwappableToken) || swap_tokens.includes(token.symbol as SwappableToken);
		if (swapping) {
			set({ swapping, swapWithdrawToken: token });
			return;
		}
		set({ swapping, depositToken: token, depositAmount: "" });
	},

	toUSD: (token, amount) => {
		try {
			const { ethereum_price, rates } = get();
			switch (token.label) {
				case "ETH":
					return bi(n(ethereum_price) * n(amount));
				default:
					if (token.label in rates) {
						const rate = rates[token.label as ExecutableToken];
						// console.log(`EscrowStore::toUSD | got rate for "${token.symbol}"|${amount}`, rate);
						if (typeof rate === "bigint") {
							const result = bi(n(ethereum_price) * n(amount) * n(rate));
							// console.log(`EscrowStore::toUSD | "${token.symbol}"|${amount}`, result);
							return result;
						}
						throw new Error(`a valid conversion rate for token "${token}" could not be found.`);
					}
					throw new Error(`token "${token.label}" not found in the rates collection`);
			}
		} catch (err) {
			throw new Error(`EscrowStore::toUSD failed - ${err instanceof Error ? err.message : "no error instance"}`);
		}
	},

	deposit: async (signer): Promise<ResolverResult> => {
		try {
			set({ busy: true });
			const { availables, depositAmount, depositToken, toUSD } = get();

			if (depositToken) {
				//get the wallet balance for the subject token, erroring if a value isn't present.
				const available = availables[depositToken.label];
				if (typeof available !== "bigint") throw new Error(`availables for token "${depositToken.label}" not available`);

				const amountFromString = bi(Number.parseFloat(sn(depositAmount)));
				const amount = roundToBalance(amountFromString, available);
				const amountUSD = n(toUSD(depositToken, amount));

				// deposit to small.
				if (amountUSD < 95) {
					set({ busy: false });
					return { ok: false, status: "warning", message: "Minimum deposit amount is $100USD, please increase your deposit.", code: "UNDERFUND" };
				}

				// deposit exceeds balance
				if (amount > available) {
					set({ busy: false });
					return { ok: false, status: "warning", message: `Not enough funds, swap more funds into ${depositToken.label}`, code: "UNDERFUND" };
				}
				if (signer) {
					await signer();
				}
				const response = await R.deposit({ amount, token: depositToken });
				if (response.ok) {
					const { hash } = response;
					set({ depositAmount: "0" });

					const { address, chainId: chain } = getAccount(wagmiConfig);
					const contractAddress = lookupContract(chain, "Escrow");
					const trackParams = {
						eventType: "escrow_deposit",
						eventName: "escrow_deposit",
						parameters: {
							walletAddress: address,
							amount: n(amount),
							amountUSD,
							currency: depositToken.symbol,
							contractAddress,
						},
					};

					safary.track(trackParams);

					// console.log("safary track deposit", trackParams);

					GAEvent({
						category: "escrow_deposit",
						action: "escrow_deposit",
						label: depositToken.symbol,
						value: amountUSD,
					});

					if (window.cookie3) {
						window.cookie3.trackEvent({
							category: "escrow",
							action: "deposit",
							name: depositToken.symbol,
							value: amountUSD,
						});
						console.log("cookie3 deposit dispatch");
					} else console.warn("failed tracking to cookie3");

					set({ busy: false });

					return response;
				}
				set({ busy: false });
				return response;
			}
			throw new Error("EscrowStore::deposit - depositToken not set");
		} catch (err) {
			set({ busy: false });
			console.warn(`deposit failed - ${err instanceof Error ? err.message : "no error instance present"}`);
			return {
				ok: false,
				status: "error",
				message: "Your deposit was not successful",
				code: "UNHANDLED",
			};
		}
	},

	swap: async (signer) => {
		try {
			set({ busy: true });

			const {
				availables,
				balances,
				ethereum_balance,
				swapDepositAmount,
				swapDepositToken,
				swapWithdrawAmount,
				swapWithdrawToken,
				slippage,
				impact,
				swap_minimum,
				toUSD,
			} = get();

			if (!swapDepositToken) throw new Error("swapDepositToken not set");
			if (!swapWithdrawToken) throw new Error("swapWithdrawToken not set");

			const withdrawAmount = bi(Number.parseFloat(sn(swapWithdrawAmount)));
			const swapWithdrawAmountUSD = n(toUSD(swapWithdrawToken, withdrawAmount));

			const balance = swapWithdrawToken.symbol === "ETH" ? ethereum_balance : swapWithdrawToken.symbol === "WETH" ? availables[swapWithdrawToken.label] : balances[swapWithdrawToken.label];

			if (typeof balance !== "bigint") throw new Error(`failed gaining a balance for "${swapWithdrawToken.label}"`);

			// deposit to small.
			if (swapWithdrawAmountUSD < 95) {
				set({ busy: false });
				return { ok: false, status: "warning", message: "Minimum deposit amount is $100USD, please increase your deposit.", code: "UNDERFUND" };
			}

			// deposit exceeds balance
			if (withdrawAmount > balance) {
				set({ busy: false });
				return { ok: false, status: "warning", message: `Not enough funds, swap more funds into ${swapWithdrawToken.label}`, code: "UNDERFUND" };
			}

			if (signer) {
				await signer();
			}

			const amount_in = Number(swapWithdrawAmount);
			const min_amount_out = Number(swapDepositAmount) * 0.97;

			const response = await R.swap({
				token: swapDepositToken,
				source: swapWithdrawToken,
				amount_in,
				min_amount_out,
			});

			if (response) {
				if (typeof response.ok === "boolean") {
					set({ busy: false });

					const { address, chainId } = getAccount(wagmiConfig);
					const contractAddress = lookupContract(chainId, "EscrowRouter");
					const trackParams = {
						eventType: "escrow_swap",
						eventName: "escrow_swap",
						parameters: {
							walletAddress: address,
							fromAmount: Number(swapDepositAmount),
							fromAmountUSD: swapWithdrawAmountUSD,
							fromCurrency: swapWithdrawToken.symbol,
							toCurrency: swapDepositToken.symbol,
						},
					};
					safary.track(trackParams);
					// console.log("safary track swap", trackParams);
					GAEvent({
						category: "escrow_swap",
						action: "escrow_swap",
						label: swapWithdrawToken.symbol,
						value: swapWithdrawAmountUSD,
					});

					if (window.cookie3) {
						window.cookie3.trackEvent({
							category: "escrow",
							action: "swap",
							name: swapWithdrawToken.symbol,
							value: swapWithdrawAmountUSD,
						});
						console.log("cookie3 swap dispatch");
					} else console.warn("failed tracking to cookie3");

					return response;
				}
			}

			set({ busy: false });
			return { status: "error", message: "There was a problem completing the swap, please try again later.", code: "UNHANDLED" };
		} catch (err) {
			set({ busy: false });
			console.warn(`swap failed - ${err instanceof Error ? err.message : "no error instance present"}`);
			return { status: "error", message: "There was a problem completing the swap, please try again later.", code: "UNHANDLED" };
		}
	},

	withdraw: async () => {

		try {

			set({
				busy: true,
			});

			const {
				balances,
				rates,
				withdrawInToken: token,
				withdrawAmount,
				toUSD,
				ethereum_price,
			} = get();

			if (!token)
				throw new Error("EscrowStore::withdraw - withdrawInToken not set");

			const balance = balances[token.label];
			const amountFromString = bi(Number.parseFloat(withdrawAmount.length ? withdrawAmount : "0"));
			const amount = roundToBalance(amountFromString, balance);
			
			if (typeof balance !== "bigint")
				throw new Error(`EscrowStore::withdraw -- cannot find balance for token "${token.symbol}"`);

			// value is 0 or less.
			if (amount <= 0) {
				set({ busy: false });
				return {
					ok: false,
					status: "error",
					message: "Attempted to withdraw zero funds. Please enter an amount and try again.",
					code: "UNDERFUND",
				};
			}

			if (amount > balance) {
				set({ busy: false });
				return {
					ok: false,
					status: "error",
					message: "Requested amount exceeds balance.",
					code: "UNDERFUND",
				};
			}

			// if the amount is basically the balance, we use that instead to avoid rounding issues
			const response = await R.withdraw({ amount, token });

			if (response.ok) {
				set({ withdrawAmount: "0" });
				const { address, chainId: chain } = getAccount(wagmiConfig);
				const amountUSD = n(toUSD(token, amount));
				const contractAddress = lookupContract(chain, "Escrow");

				const trackParams = {
					eventType: "escrow_withdraw",
					eventName: "escrow_withdraw",
					parameters: {
						walletAddress: address,
						amount: n(amount),
						amountUSD,
						currency: token.symbol,
						contractAddress,
					},
				};

				safary.track(trackParams);
				// console.log("safary track withdraw", trackParams);

				GAEvent({
					category: "escrow_withdraw",
					action: "escrow_withdraw",
					label: token.symbol,
					value: amountUSD,
				});

				if (window.cookie3) {
					window.cookie3.trackEvent({
						category: "escrow",
						action: "withdraw",
						name: token.symbol,
						value: amountUSD,
					});
					console.log("cookie3 withdraw dispatch");
				} else console.warn("failed tracking to cookie3");

				set({ busy: false });
				return response;
			}
			set({ busy: false });
			return response;
		} catch (err) {
			set({ busy: false });
			console.warn(`withdraw failed - ${err instanceof Error ? err.message : "no error instance present"}`);
			return { ok: false, message: "withdrawal failed due to a server error, please try again later." };
		}
	},

	getTokenAvailable: (token) => {
		const { availables, ethereum_balance } = get();
		switch (token.label) {
			case "ETH":
				return ethereum_balance;
			default:
				if (token.label in availables && typeof availables[token.label as ExecutableToken] === "bigint") {
					return availables[token.label as ExecutableToken] as bigint;
				}
				console.log(`no balance for "${token.label}"`, availables);
				throw new Error(`no balance available for "${token.label}"`);
		}
	},

	getTokenBalanceUSD: (token) => {
		const { ethereum_balance, ethereum_price, balances, rates } = get();
		switch (token.label) {
			case "ETH":
			case "wETH":
				return bi(n(ethereum_balance) * n(ethereum_price));
			default:
				if (token.label in balances && typeof balances[token.label as ExecutableToken] === "bigint") {
					const balance = balances[token.label as ExecutableToken] as bigint;
					if (token.label in rates && typeof rates[token.label as ExecutableToken] === "bigint") {
						const rate = rates[token.label as ExecutableToken] as bigint;
						return bi(n(balance) * n(rate) * n(ethereum_price));
					}
					throw new Error(`getTokenBalanceUSD:: invalid rate for "${token.label}"`);
				}
				throw new Error(`getTokenBalanceUSD:: invalid balance for "${token.label}"`);
		}
	},

	applyMaxDeposit: () => {
		const {
			availables,
			depositToken,
		} = get();
		
		if (depositToken) {
			if (depositToken.label in availables) {
				set({
					depositAmount: Number(Number(availables[depositToken.label as ExecutableToken]) / Number(1e18)).toFixed(5),
					swapWithdrawAmountMessage: "",
					swapWithdrawAmountValid: true,
				});
				return;
			}
			throw new Error(`EscrowStore::applyMaxDeposit - token "${depositToken.label}" not in the balances collection.`);
		}
		throw new Error("EscrowStore::applyMaxDeposit - depositToken not set");
	},

	applyMaxWithdraw: () => {
		const {
			balances,
			withdrawInToken,
		} = get();
		if (!withdrawInToken) throw new Error("EscrowStore::applyMaxWithdraw - withdrawInToken not set");
		const { label, decimals } = withdrawInToken;
		if (label in balances) {
			set({ withdrawAmount: Number(Number(balances[label]) / Number(`1e${decimals}`)).toFixed(5) });
		}
	},

	applyMaxSwap: () => {

		const {
			getTokenBalance,
			swapWithdrawToken,
			swapDepositToken,
		} = get();

		const {
			chainId,
		} = getAccount(wagmiConfig);

		if(!GAS_FEE_MAP.has(chainId))
			console.warn(`EscrowStore::applyMaxSwap - applying 0.01 as gas fee as chain [${chainId}] is not mapped`);

		const gasFee = GAS_FEE_MAP.get(chainId) || 0.01;

		if (!swapWithdrawToken) throw new Error("EscrowStore::applyMaxSwap - swapWithdrawToken not set");
		if (!swapDepositToken) throw new Error("EscrowStore::applyMaxSwap - swapDepositToken not set");
		const balance = getTokenBalance(swapWithdrawToken);
		const parsedNumber = n(balance) - gasFee;
		const amountString = (parsedNumber > 0 ? parsedNumber : 0).toFixed(5);

		set({ swapWithdrawAmount: amountString });
		(async () => {
			setTimeout(async () => {
				const { swapWithdrawAmount, swapDepositToken } = get();
				const result = await R.quote({ token: swapDepositToken, amount: swapWithdrawAmount });
				set({ swapDepositAmount: Number(Number(result) / Number(1e18)).toFixed(5) });
			}, 0);
		})();
	},

	// ----
	// PRIVATE METHODS
	// ----

	_initializePolling: () => {
		const { _pollInterval, _poll } = get();

		if (_pollInterval) clearInterval(_pollInterval);

		const poll = setInterval(_poll, POLL_INTERVAL);
		set({
			_pollInterval: poll,
		});

		// console.log("polling initialized");
	},

	_loadData: async (force = false) => {

		const {
			address,
			chain: accountChain,
		} = getAccount(wagmiConfig);

		const load_start = new Date().getTime();

		const chain = accountChain?.id;

		// as we are doing 5+ concurrent calls, the user may well just disconnect, or change chain or...
		// so here we set a flag which, when catch the error, can evaluate the state of change and determine that
		// the user interrupted mid transaction, can assume all is ok and not throw the caught errors.
		let interrupted = false;

		if (!address || !chain) {
			get()._stopPolling();
			await get()._resetData();
			return;
		}

		try {

			const [loaders, cancelDataLoad] = makeCancellablePromise(
				Promise.all([
					get()._loadEthereumBalance(force),
					get()._loadEthereumPrice(force),
					get()._loadRatesForChain(force),
					get()._loadUserBalances(force),
					get()._loadAvailables(force),
					get()._loadTotalStaked(force),
					get()._loadTotalUserStaked(force),
					get()._loadTotalUserStakedOnChain(force)
				]),
			);
			set({ cancelDataLoad });
			await loaders;
			set({ cancelDataLoad: null });
			const load_end = new Date().getTime();
			console.log(`load --- ${load_end-load_start}ms`);
			return;
		} catch (err) {
			// console.error('EscrowStore: _loadData error', err)

			get()._stopPolling();
			await get()._resetData();
		}
	},

	_handleErrorDataLoad: async (loader: string, err: Error) => {
		const { address, connector } = getAccount(wagmiConfig);
		// const connectors = getConnectors(wagmiConfig);
		const usedWalletId = connector?.id;
		const usedWalletAddress = address;
		const userAgent = navigator.userAgent;

		const report = {
			errorType: "escrow-load-error",
			loader,
			errorMessage: err.message,
			trace: err.stack,
			usedWalletId,
			usedWalletAddress,
		};

		fetch("/crash", {
			method: "POST",
			body: JSON.stringify(report),
			headers: {
				"Content-Type": "application/json",
			},
		}).then((r) => {
			const now = moment().format().replace("T", " ");
			console.log(`derived crash report filed [${now}]`, report);
		});
	},

	_loadEthereumBalance: async (force = false) => {
		try {
			const { address, chain } = getAccount(wagmiConfig);
			if (address) {
				const balance = await R.getEthereumBalance({ wallet: address, force });
				if (typeof balance === "bigint") {
					set({
						ethereum_balance: balance,
						ethereum_balance_result_since: new Date().getTime(),
					});
					return;
				}
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadEthereumBalance", err);
		}
	},

	_loadEthereumPrice: async (force = false) => {
		try {
			const now = moment().unix();
			const { chain } = getAccount(wagmiConfig);
			const { ethereum_price_since } = get();

			// ethereum price loaded every minute.
			if (chain?.id && now - ethereum_price_since > 360) {
				const ethereum_price = await R.getEthereumPrice({ chain: chain.id, force });
				if (typeof ethereum_price === "bigint") {
					set({
						ethereum_price,
						ethereum_price_since: new Date().getTime(),
					});
					return;
				}
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadEthereumPrice", err);
		}
	},

	_loadAvailables: async (force = false) => {
		try {
			const { address } = getAccount(wagmiConfig);
			if (address) {
				const data = await R.getAvailables({ force });
				if (data && Object.values(data).every((v) => typeof v === "bigint")) {
					set({
						availables: data,
						availables_since: new Date().getTime(),
					});
					return;
				}
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadAvailables", err);
		}
	},

	_loadTotalStaked: async (force = false) => {
		try {
			const total_stake = await R.totalStaked({ force });
			if (typeof total_stake === "bigint") {
				set({
					total_stake,
					total_since: new Date().getTime(),
				});
				return;
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadTotalStaked", err);
		}
	},

	_loadTotalUserStaked: async (force = false) => {
		try {
			const { address, chain } = getAccount(wagmiConfig);
			if (address && chain.id) {
				const data = await R.totalUserStaked({ chain: chain.id, wallet: address, force });
				if (typeof data === "bigint") {
					set({
						user_stake: data,
						user_stake_since: new Date().getTime(),
					});
					return;
				}
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadTotalUserStaked", err);
		}
	},

	_loadTotalUserStakedOnChain: async (force = false) => {
		try {
			const { address, chain } = getAccount(wagmiConfig);
			if (address && chain.id) {
				const data = await R.totalUserStakedOnChain({ chain: chain.id, wallet: address, force });
				if (typeof data === "bigint") {
					set({
						user_stake_chain: data,
						user_stake_chain_since_since: new Date().getTime(),
					});
					return;
				}
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadTotalUserStakedOnChain", err);
		}
	},

	_loadUserBalances: async (force = false) => {
		try {
			const { address, chain } = getAccount(wagmiConfig);
			if (address && chain.id) {
				const data = await R.getUserBalances({ chain: chain.id, wallet: address, force });
				if (Object.values(data).every((v) => typeof v === "bigint")) {
					set({
						balances: data,
						balances_since: new Date().getTime(),
					});
					return;
				}
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadUserBalances", err);
		}
	},

	_loadRatesForChain: async () => {
		try {
			const now = moment().unix();
			const { address, chain } = getAccount(wagmiConfig);
			const { rates_since } = get();
			// load rates once per hour.
			if (chain.id && now - rates_since > 3600) {
				const data = await R.getRatesForChain({
					chain: chain.id,
				});
				set({
					rates: data,
					rates_since: moment().unix(),
				});
				console.log(" >>> binding rates to global zates")
				globalThis.zates = data;

				return;
			}
		} catch (err) {
			await get()._handleErrorDataLoad("_loadRatesForChain", err);
		}
	},

	_poll: async () => {
		const { address, chainId } = getAccount(wagmiConfig);
		if (address && chainId) {
			const { _loadData } = get();
			await _loadData();
		} else {
			console.log("poll aborted - unexpected chain state, skipping.");
		}
	},

	_resetData: async () => {
		get()._resetDeposit();
		get()._resetSwap();
		set({
			ethereum_balance: BigInt(-1),
			initialized: false,
		});
	},

	_resetSwap: () => {
		const chainId = getChainId(wagmiConfig);
		set({
			swapDepositAmount: "",
			swapDepositToken: getChainedToken(chainId, "ETH"),
			swapWithdrawAmount: "",
			swapWithdrawAmountValid: true,
			swapWithdrawAmountMessage: "",
			swapWithdrawToken: getChainedToken(chainId, "WSTETH"),
			slippage: "0",
			impact: "0",
		});
		return;
	},

	_resetDeposit: () => {
		// console.log(`EscrowStore::_resetDeposit`);
	},

	_stopPolling: () => {
		const { _pollInterval } = get();
		if (_pollInterval) {
			clearInterval(_pollInterval);
			set({ _pollInterval: null });
		}
	},

	_toggleBusy: () => {
		// console.log("EscrowStore::_toggleBusy");
		const { busy } = get();
		set({ busy: !busy });
	},

	_cancelDataLoad: () => {
		const { cancelDataLoad } = get();
		if (cancelDataLoad) {
			cancelDataLoad();
		}
		set({ cancelDataLoad: false });
	},

	_onAccountChange: async (
		{ status, address, chain },
		{ status: prev_status, address: prev_address, chain: prev_chain }) => {

		const {
			_cancelDataLoad,
			_loadChainedTokens,
			_resetSwap,
			_stopPolling,
			_initializePolling,
			deinitialize,
		} = get();

		_cancelDataLoad();

		switch(status) {

			case "reconnecting":
			case "connecting":
				// wait for a connected or disconnected event to appear.
				return;

			case "disconnected":
				deinitialize();
				return;

			case "connected":
				set({
					initializing: true,
					rates_since: -1,
				});
				_stopPolling();
				_resetSwap();		
				if(address && chain) {

					// they've changed chain, lets just hard reset the view.
					if(prev_chain && chain !== prev_chain) {
						window.location.reload();
						return;
					}

					_loadChainedTokens();
					await get()._loadData(true);
					return;
				}
				// we dont have what we expect - a valid chain and address.
				_stopPolling();
				await get()._resetData();
				return;
			default:
				throw new Error(`wagmi produced an unknown status "${status}"`)
		}

		// if (status === "disconnected") {
		// 	return;
		// }

		// if(status === "reconnecting") {
		// 	return;
		// }

		// if(status === "connecting")

		// // force reload of rates - for now.


		// if (!address) {
		// 	_cancelDataLoad();
		// }

		// if (address && chain && prev_chain && chain !== prev_chain) {
		// 	_cancelDataLoad();
		// 	window.location.reload();
		// 	return;
		// }

		// if (address && chain) {
		// 	_loadChainedTokens();
		// 	await get()._loadData(true);
		// 	set({ initializing: false });
		// 	_initializePolling();
		// } else {
		// 	_stopPolling();
		// 	await get()._resetData();
		// }
	},
}));

const BigIntKeys: Partial<keyof EscrowStore>[] = [];
